<!-- 正式推送页面 -->
<template>
  <div class="step-page official-push">
    <div class="step-container">
      <!-- 步骤进度条 -->
      <module-container title="制作步骤">
        <step-progress
          :current-step="$route.query.type == 3 ? 4 : 5"
          :steps="progressPages"
        />
      </module-container>
      <!-- 主要内容区域 -->
      <div class="step-content">
        <module-container title="正式推送">
          <div class="official-push-form">
            <!-- 提示信息 -->
            <div class="tip-section">
              <span class="tip-label">提示</span>
              <span class="tip-content">
                您即将对<span class="push-target">{{ pushTarget }}</span
                >进行正式推送
              </span>
            </div>
            <!-- 表单 -->
            <el-form
              :model="pushForm"
              :rules="formRules"
              ref="pushForm"
              label-width="120px"
            >
              <!-- 手机号 -->
              <el-form-item label="手机号" prop="mobile">
                <el-input
                  v-model="pushForm.mobile"
                  placeholder="请输入手机号"
                  clearable
                  maxlength="11"
                  :disabled="pushing"
                />
              </el-form-item>
              <!-- 验证码 -->
              <el-form-item label="验证码" prop="verifyCode">
                <div class="verify-code-wrapper">
                  <el-input
                    v-model="pushForm.verifyCode"
                    placeholder="请输入验证码"
                    clearable
                    maxlength="6"
                    :disabled="pushing"
                  />
                  <el-button
                    :disabled="codeCountdown > 0 || gettingVerifyCode"
                    :loading="gettingVerifyCode"
                    @click="handleGetVerifyCode"
                  >
                    {{
                      codeCountdown > 0
                        ? `${codeCountdown}秒后重新获取`
                        : '获取验证码'
                    }}
                  </el-button>
                </div>
              </el-form-item>
            </el-form>
            <!-- 操作按钮 -->
            <div class="action-buttons">
              <el-button :disabled="pushing" @click="handlePrev">
                上一步
              </el-button>
              <el-button
                type="primary"
                :loading="pushing"
                :disabled="pushing"
                @click="handleConfirm"
              >
                正式推送
              </el-button>
            </div>
          </div>
        </module-container>
      </div>
    </div>
  </div>
</template>
<script>
import ModuleContainer from '@/components/moduleContainer'
import StepProgress from '@/components/stepProgress'
import { Get, ProjectConfig } from '@/api'
import { mapState } from 'vuex'

// 手机号验证正则
const MOBILE_REG = /^1[3-9]\d{9}$/
// 验证码验证正则
const VERIFY_CODE_REG = /^\d{4,6}$/
// 倒计时时长（秒）
const COUNTDOWN_DURATION = 60

/**
 * 正式推送页面组件
 * 功能：第六步 - 执行正式推送
 * - 获取手机验证码（带倒计时）
 * - 执行正式推送操作
 */
export default {
  name: 'OfficialPush',
  components: {
    ModuleContainer,
    StepProgress
  },
  data() {
    return {
      operatorName: '', // 操作人姓名
      pushing: false, // 推送中状态
      gettingVerifyCode: false, // 获取验证码中状态
      codeCountdown: 0, // 验证码倒计时
      countdownTimer: null, // 倒计时定时器
      // 表单数据
      pushForm: {
        mobile: '',
        verifyCode: ''
      },
      // 表单验证规则
      formRules: {
        mobile: [
          {
            required: true,
            validator: this.validateMobile,
            trigger: 'blur'
          }
        ],
        verifyCode: [
          {
            required: true,
            validator: this.validateVerifyCode,
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    ...mapState('micro/user', { USERINFO: 'userInfo' }),
    ...mapState('incrementService', ['progressPages']),
    // 从 store 获取当前任务信息
    taskInfo() {
      return this.$store.getters['incrementService/getCurrentTaskInfo']
    },
    // 任务ID
    taskId() {
      return this.$route.query.taskId || this.taskInfo?.id || null
    },
    // 推送目标
    pushTarget() {
      return this.taskInfo?.topic || '当前任务'
    }
  },
  mounted() {
    this.initData()
  },
  beforeDestroy() {
    this.clearCountdown()
  },
  methods: {
    /**
     * 初始化数据
     */
    initData() {
      this.operatorName = this.USERINFO?.loginName || ''
    },

    /**
     * 表单验证：手机号
     */
    validateMobile(rule, value, callback) {
      if (!value) {
        callback(new Error('请输入手机号'))
      } else if (!MOBILE_REG.test(value)) {
        callback(new Error('请输入正确的手机号'))
      } else {
        callback()
      }
    },

    /**
     * 表单验证：验证码
     */
    validateVerifyCode(rule, value, callback) {
      if (!value) {
        callback(new Error('请输入验证码'))
      } else if (!VERIFY_CODE_REG.test(value)) {
        callback(new Error('请输入正确的验证码'))
      } else {
        callback()
      }
    },

    /**
     * 验证手机号格式（用于获取验证码前）
     */
    checkMobileFormat(mobile) {
      if (!mobile) {
        this.$message.warning('请先输入手机号')
        return false
      }
      if (!MOBILE_REG.test(mobile)) {
        this.$message.error('请输入正确的手机号')
        return false
      }
      return true
    },

    /**
     * 开始倒计时
     */
    startCountdown() {
      this.clearCountdown()
      this.codeCountdown = COUNTDOWN_DURATION
      this.countdownTimer = setInterval(() => {
        this.codeCountdown--
        if (this.codeCountdown <= 0) {
          this.clearCountdown()
        }
      }, 1000)
    },

    /**
     * 清除倒计时
     */
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    /**
     * 获取验证码
     * API: GET /api/verifyCode/smsCode?receiver={mobile}
     */
    async handleGetVerifyCode() {
      // 防止重复获取
      if (this.codeCountdown > 0) return
      // 验证手机号格式
      if (!this.checkMobileFormat(this.pushForm.mobile)) return

      this.gettingVerifyCode = true
      try {
        const res = await Get(ProjectConfig.increment.GetSmsVerifyCode.url, {
          params: { receiver: this.pushForm.mobile, taskId: this.taskId }
        })

        if (res?.data?.status === 0) {
          this.$message.success('验证码已发送')
          this.startCountdown()
        } else {
          this.$message.error(res?.data?.message || '获取验证码失败')
        }
      } catch (error) {
        console.error('获取验证码失败：', error)
        this.$message.error('获取验证码失败，请稍后重试')
      } finally {
        this.gettingVerifyCode = false
      }
    },

    /**
     * 确认推送：表单验证通过后执行
     */
    handleConfirm() {
      this.$refs.pushForm.validate((valid) => {
        if (valid) {
          this.executePush()
        }
      })
    },

    /**
     * 执行推送
     * API: GET /api/task/push/send
     */
    async executePush() {
      // 验证必要参数
      if (!this.taskId) {
        this.$message.error('缺少任务ID')
        return
      }
      if (!this.operatorName) {
        this.$message.error('缺少操作人信息')
        return
      }

      this.pushing = true
      const namespace = window.location.origin
      try {
        const res = await Get(ProjectConfig.increment.OfficialPush.url, {
          params: {
            taskId: this.taskId,
            operatorName: this.operatorName,
            receiver: this.pushForm.mobile,
            code: this.pushForm.verifyCode,
            namespace: namespace + '/das-manager'
          }
        })

        if (res?.data?.status === 0) {
          this.$message.success('正式推送成功')
          setTimeout(() => {
            this.$router.push({
              path: '/newActivePushEmail',
              query: {
                columnId: '10-2',
                type: this.$route.query.type
              }
            })
          }, 1000)
        } else {
          this.$message.error(res?.data?.message || '正式推送失败')
          this.resetVerifyCode()
        }
      } catch (error) {
        console.error('推送失败：', error)
        this.$message.error('推送失败，请稍后重试')
        this.resetVerifyCode()
      } finally {
        this.pushing = false
      }
    },

    /**
     * 重置验证码状态
     */
    resetVerifyCode() {
      this.pushForm.verifyCode = ''
      this.clearCountdown()
      this.codeCountdown = 0
    },

    /**
     * 上一步：跳转到推送测试页面
     */
    handlePrev() {
      this.$router.push({
        path: '/steps/pushTest',
        query: {
          ...this.$route.query
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.step-page {
  height: 100%;
  overflow: hidden;
  background: #f5f7fd;

  .step-container {
    height: 100%;
    overflow-y: auto;

    .step-content {
      .module-body {
        min-height: 300px;
        box-sizing: border-box;
      }

      .official-push-form {
        display: flex;
        flex-direction: column;
        align-items: center;

        .el-form {
          width: 100%;
          max-width: 520px;
        }

        ::v-deep .el-form-item {
          margin-bottom: 22px;
        }

        ::v-deep .el-form-item__label {
          text-align: right;
          padding-right: 12px;
          font-size: 14px;
        }

        .el-input {
          width: 300px;
          font-size: 14px;
        }

        .tip-section {
          margin-bottom: 30px;
          padding: 12px 16px;
          background: #ecf5ff;
          border-left: 4px solid #4054af;
          border-radius: 4px;

          .tip-label {
            color: #4054af;
            font-weight: 600;
            margin-right: 8px;
          }

          .tip-content {
            color: #303133;
            font-size: 14px;

            .push-target {
              color: #4054af;
              font-weight: 600;
              display: inline-block;
              padding: 0 4px;
            }
          }
        }

        .verify-code-wrapper {
          display: flex;
          align-items: center;

          .el-input {
            margin-right: 10px;
          }
        }

        .action-buttons {
          margin-top: 30px;
          text-align: center;

          .el-button {
            margin: 0 10px;
            min-width: 100px;
          }
        }
      }
    }
  }
}
</style>
